import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notifyError } from '../ui/Toasts.js'
import { api, type OfficeRapport, type RapportFilter, type TenantUser } from '../net/apiClient.js'
import { useCacheTable } from '../ui/hooks.js'
import type { CacheRow } from '../net/localDb.js'

/**
 * Rapportliste für das Büro.
 *
 * Der Superuser hat hier genau eine Frage: was ist hereingekommen und noch
 * nicht verrechnet. Alles andere sind Nebenfragen, und sie stehen deshalb in
 * einer Filterzeile statt in eigenen Bildschirmen.
 *
 * "Verrechnet" ist kein Feld am Rapport, sondern abgeleitet: es liegt eine
 * gültige Reservierung auf einer seiner Zeilen. Wird der Beleg storniert,
 * wandert der Rapport von selbst zurück in die Arbeitsliste – das ist der
 * ganze Grund, warum der Zustand nicht gespeichert wird.
 *
 * Gelesen wird direkt vom Server statt aus dem Cache: der Bestand an Rapporten
 * wächst schneller als der an Kunden, und das Büro sitzt am Netz.
 */

interface RapportLine {
  id: string
  kind: string
  description: string
  quantity: string
  unit: string
  unitPriceRp: number | null
  isExtra: boolean
  isFreetext: boolean
}

interface RapportPhoto {
  id: string
  bytes: number
  width: number | null
  height: number | null
  uploadedAt: string | null
}

export function Rapports({
  session,
  onVerrechnen
}: {
  session: SessionUser
  /** Übergibt die gewählten Rapportzeilen an den Belegbildschirm. */
  onVerrechnen?: (auswahl: { customerId: string; projectId: string; lineIds: string[] }) => void
}): JSX.Element {
  const [rows, setRows] = useState<OfficeRapport[]>([])
  const [loading, setLoading] = useState(true)
  const [ladefehler, setLadefehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [filter, setFilter] = useState<RapportFilter>({ status: 'offen' })
  const [users, setUsers] = useState<TenantUser[]>([])
  const { rows: projects } = useCacheTable<CacheRow>('projects')

  const laden = useCallback(async (): Promise<void> => {
    const r = await api.listRapports(filter)
    setRows(r.rapports)
    setLoading(false)
  }, [filter])

  /*
    Ein Fehler beim Laden muss sichtbar sein.

    Hier stand `void laden().catch(() => setLoading(false))` – der Fehler war
    damit vollständig verschluckt. Antwortete der Server mit 500, sah die Liste
    aus wie ein Betrieb ohne Rapporte: «Nichts offen – alles verrechnet.» Genau
    diese Meldung stand in der Vorführung auf dem Bildschirm, und sie war
    gelogen.
  */
  useEffect(() => {
    setLoading(true)
    setLadefehler(null)
    void laden().catch((err: unknown) => {
      setLoading(false)
      setLadefehler(
        err instanceof Error ? err.message : 'Die Rapporte liessen sich nicht laden.'
      )
    })
  }, [laden])

  useEffect(() => {
    if (session.role !== 'superuser') return
    void api
      .listUsers()
      .then((r) => setUsers(r.users))
      .catch((err: unknown) => {
        setUsers([])
        // Der Monteurfilter bleibt dann leer – das darf nicht wortlos passieren.
        notifyError(err)
      })
  }, [session.role])

  if (offen) {
    return (
      <RapportDetail
        session={session}
        id={offen}
        onBack={() => {
          setOffen(null)
          void laden()
        }}
        onVerrechnen={onVerrechnen}
      />
    )
  }

  const setze = (teil: Partial<RapportFilter>): void => setFilter((f) => ({ ...f, ...teil }))

  return (
    <div className="card">
      <div className="listhead">
        <h2>Rapporte</h2>
        <span className="muted">{rows.length}</span>
      </div>

      {/*
        Umschalter statt Auswahlliste für die drei Zustände: es sind drei, sie
        schliessen sich aus, und man wechselt oft zwischen ihnen.
      */}
      <div className="segmented" role="group" aria-label="Zustand">
        {(
          [
            ['offen', 'Nicht verrechnet'],
            ['verrechnet', 'Verrechnet'],
            ['alle', 'Alle']
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            type="button"
            id={`filter-${wert}`}
            className={filter.status === wert ? 'on' : ''}
            aria-pressed={filter.status === wert}
            onClick={() => setze({ status: wert })}
          >
            {text}
          </button>
        ))}
      </div>

      <div className="filterrow">
        <button
          type="button"
          id="filter-signiert"
          className={`chip ${filter.signed === 'ja' ? 'on' : ''}`}
          aria-pressed={filter.signed === 'ja'}
          onClick={() => setze({ signed: filter.signed === 'ja' ? undefined : 'ja' })}
        >
          Unterschrieben
        </button>
        <button
          type="button"
          id="filter-preis"
          className={`chip ${filter.priceOpen ? 'on' : ''}`}
          aria-pressed={filter.priceOpen === true}
          onClick={() => setze({ priceOpen: !filter.priceOpen })}
        >
          Preis offen
        </button>
        <button
          type="button"
          id="filter-fotos"
          className={`chip ${filter.withPhotos ? 'on' : ''}`}
          aria-pressed={filter.withPhotos === true}
          onClick={() => setze({ withPhotos: !filter.withPhotos })}
        >
          Mit Fotos
        </button>
      </div>

      <div className="filterrow">
        <label className="inline">
          <span className="muted small">Projekt</span>
          <select
            id="filter-projekt"
            value={filter.projectId ?? ''}
            onChange={(e) => setze({ projectId: e.target.value || undefined })}
          >
            <option value="">alle</option>
            {projects.map((p) => (
              <option key={String(p.id)} value={String(p.id)}>
                {String(p.name)}
              </option>
            ))}
          </select>
        </label>
        <label className="inline">
          <span className="muted small">Monteur</span>
          <select
            id="filter-monteur"
            value={filter.userId ?? ''}
            onChange={(e) => setze({ userId: e.target.value || undefined })}
          >
            <option value="">alle</option>
            {users.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="muted">Wird geladen …</p>
      ) : ladefehler ? (
        <div className="stack" id="rapporte-fehler" role="alert">
          <p>
            <strong>Die Rapporte liessen sich nicht laden.</strong> {ladefehler}
          </p>
          <ActionButton
            id="btn-rapporte-nochmal"
            variant="secondary"
            labels={{ idle: 'Nochmal versuchen', busy: 'Lädt …' }}
            onAction={async () => {
              setLoading(true)
              setLadefehler(null)
              await laden()
            }}
            onError={(err) => {
              setLoading(false)
              setLadefehler(err instanceof Error ? err.message : 'Unbekannter Fehler.')
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <p className="muted" id="rapporte-leer">
          {filter.status === 'offen'
            ? 'Nichts offen – alles verrechnet.'
            : 'Kein Rapport passt zu diesem Filter.'}
        </p>
      ) : (
        <ul className="records">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" id={`rapport-${r.id}`} onClick={() => setOffen(r.id)}>
                <span className="rec-name">
                  {r.projectName}
                  {r.billed ? (
                    <span className="pill small superuser">verrechnet</span>
                  ) : (
                    Number(r.openLineCount) < Number(r.lineCount) && (
                      <span className="pill small warn">teilweise verrechnet</span>
                    )
                  )}
                  {r.signed && <span className="pill small">unterschrieben</span>}
                  {r.isRegie && <span className="pill small">Regie</span>}
                  {Number(r.openPriceCount) > 0 && (
                    <span className="pill small warn">Preis offen</span>
                  )}
                </span>
                <span className="rec-sub">
                  {r.workDate} · {r.userName} · {r.customerName}
                  {Number(r.hours) > 0 ? ` · ${Number(r.hours)} h` : ''}
                  {Number(r.photoCount) > 0 ? ` · ${r.photoCount} Fotos` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <ActionButton
        variant="secondary"
        labels={{ idle: 'Neu laden', busy: 'Lädt …', success: 'Aktuell' }}
        onAction={laden}
        onError={(err) => notifyError(err)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------

function RapportDetail({
  session,
  id,
  onBack,
  onVerrechnen
}: {
  session: SessionUser
  id: string
  onBack: () => void
  onVerrechnen?: (auswahl: { customerId: string; projectId: string; lineIds: string[] }) => void
}): JSX.Element {
  const [data, setData] = useState<{
    rapport: OfficeRapport & { status: string; projectId: string }
    lines: RapportLine[]
    photos: RapportPhoto[]
  } | null>(null)
  /** Nur die offenen Zeilen dieses Rapports – verrechnete sind schon weg. */
  const [offeneIds, setOffeneIds] = useState<Set<string>>(new Set())
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  const [customerId, setCustomerId] = useState<string>('')

  useEffect(() => {
    void (async () => {
      /*
        Auch hier: der frühere `.catch(() => null)` liess den Bildschirm für
        immer auf «Wird geladen …» stehen, ohne dass irgendwo stand, warum.
      */
      const r = await api.getRapport(id).catch((err: unknown) => {
        notifyError(err)
        return null
      })
      if (!r) return setData(null)
      setData(r as unknown as typeof data)
      const projectId = String((r.rapport as Record<string, unknown>).projectId ?? '')
      const offen = await api.openLines({ projectId }).catch((err: unknown) => {
        // Ohne diese Meldung verschwindet «In Rechnungsentwurf» kommentarlos,
        // und es sieht aus, als wäre bereits alles verrechnet.
        notifyError(err)
        return { lines: [] as Awaited<ReturnType<typeof api.openLines>>['lines'] }
      })
      const meine = offen.lines.filter((l) => l.rapportId === id)
      setOffeneIds(new Set(meine.map((l) => l.id)))
      // Vorausgewählt ist alles, was offen ist: der übliche Fall ist "den
      // ganzen Rapport verrechnen", und Abwählen ist schneller als Anwählen.
      setGewaehlt(new Set(meine.map((l) => l.id)))
      setCustomerId(meine[0]?.customerId ?? '')
    })()
  }, [id])

  if (!data) {
    return (
      <div className="card">
        <p className="muted">Wird geladen …</p>
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Zurück', busy: '…' }}
          onAction={async () => onBack()}
        />
      </div>
    )
  }

  const { rapport, lines, photos } = data
  const seesPrices = session.role === 'superuser' || session.workerSeesPrices
  const stunden = lines
    .filter((l) => l.kind === 'stunden')
    .reduce((s, l) => s + Number(l.quantity), 0)
  const kannVerrechnen =
    session.role === 'superuser' && onVerrechnen !== undefined && offeneIds.size > 0

  const umschalten = (lineId: string): void => {
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(lineId)) neu.delete(lineId)
      else neu.add(lineId)
      return neu
    })
  }

  return (
    <>
      <div className="card slim">
        <button type="button" id="btn-rapport-zurueck" className="backlink" onClick={onBack}>
          ← Rapporte
        </button>
      </div>

      <div className="card">
        <div className="listhead">
          <h2>{rapport.projectName}</h2>
          <span className="muted">{rapport.workDate}</span>
        </div>
        <dl>
          <dt>Kunde</dt>
          <dd>{rapport.customerName}</dd>
          <dt>Erfasst von</dt>
          <dd>{rapport.userName}</dd>
          <dt>Stunden</dt>
          <dd>{stunden}</dd>
          <dt>Zustand</dt>
          <dd>
            {/*
              Die Reihenfolge ist die Reihenfolge der Sperren: unterschrieben
              wiegt schwerer als verrechnet, und beides schwerer als offen. Wer
              hier "offen · noch änderbar" liest, muss auch ändern können –
              sonst läuft er in eine Fehlermeldung, die er nicht erwartet hat.
            */}
            {rapport.signed ? (
              <span className="pill superuser">unterschrieben · gesperrt</span>
            ) : rapport.billed ? (
              <span className="pill superuser">verrechnet · gesperrt</span>
            ) : Number(rapport.openLineCount) < Number(rapport.lineCount) ? (
              <span className="pill warn">teilweise verrechnet · gesperrt</span>
            ) : (
              <span className="pill">offen · noch änderbar</span>
            )}
          </dd>
          {rapport.signedByName && (
            <>
              <dt>Unterschrift</dt>
              <dd>{rapport.signedByName}</dd>
            </>
          )}
        </dl>
        {rapport.description && <p>{rapport.description}</p>}
      </div>

      <div className="card">
        <h2>Zeilen</h2>
        {kannVerrechnen && (
          <p className="muted small">
            Angehakte Zeilen kommen in den Rechnungsentwurf. Bereits verrechnete Zeilen lassen sich
            nicht mehr anhaken.
          </p>
        )}
        <ul className="records">
          {lines.map((l) => {
            const offen = offeneIds.has(l.id)
            return (
              <li key={l.id}>
                <div className="line-row">
                  {kannVerrechnen && (
                    <label className="pickbox">
                      <input
                        autoComplete="off"
                        type="checkbox"
                        id={`pick-line-${l.id}`}
                        checked={gewaehlt.has(l.id)}
                        disabled={!offen}
                        onChange={() => umschalten(l.id)}
                      />
                    </label>
                  )}
                  <span className="rec-name">
                    {l.description}
                    {l.isFreetext && <span className="pill small">Freitext</span>}
                    {l.isExtra && <span className="pill small">Regie</span>}
                    {!offen && kannVerrechnen && (
                      <span className="pill small superuser">verrechnet</span>
                    )}
                  </span>
                  <span className="rec-sub">
                    {Number(l.quantity)} {l.unit}
                    {seesPrices && l.unitPriceRp != null && l.unitPriceRp > 0
                      ? ` · CHF ${formatChf(l.unitPriceRp)}`
                      : l.isFreetext
                        ? ' · Preis offen'
                        : ''}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
        {lines.some((l) => l.isFreetext) && (
          <p className="muted small">
            Freitextzeilen haben noch keinen Preis. Sie sind keine Katalogpositionen – beim
            Verrechnen legst du Preis, Einheit und MWST fest.
          </p>
        )}

        {kannVerrechnen && (
          <ActionButton
            id="btn-verrechnen"
            labels={{
              idle: `In Rechnungsentwurf (${gewaehlt.size})`,
              busy: 'Legt an …',
              success: 'Entwurf offen'
            }}
            disabled={gewaehlt.size === 0}
            onAction={async () => {
              onVerrechnen?.({
                customerId,
                projectId: rapport.projectId,
                lineIds: [...gewaehlt]
              })
            }}
            onError={(err) => notifyError(err)}
          />
        )}
      </div>

      <div className="card">
        <h2>Fotos</h2>
        {photos.length === 0 ? (
          <p className="muted">Keine Fotos.</p>
        ) : (
          <div className="thumbs">
            {photos.map((p) => (
              <img
                key={p.id}
                className="thumb"
                src={`/rapports/${id}/photos/${p.id}`}
                alt={`Foto ${Math.round(p.bytes / 1024)} KB`}
                loading="lazy"
              />
            ))}
          </div>
        )}
      </div>

      {rapport.signed && (
        <div className="card">
          <h2>Unterschrift</h2>
          <img className="signature" src={`/rapports/${id}/signature`} alt="Unterschrift" />
        </div>
      )}
    </>
  )
}
